<template>
  <div class="form-input" :class="[
                {
                'is-required': isRequired,
                'is-empty': isEmpty,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'is-disabled': props.disabled,
            }]">
    <div class="rating" :class="classNames">
      <button v-for="i in props.stars" :key="'rating-star-' + i" :aria-label="'rating-star-' + i" :disabled="props.disabled" type="button"
              @click="handleClickedValue(i)">
        <Icon v-if="displayValue >= (i-0.25)" class="rating-star" :icon="icons.rating_star_full"/>
        <Icon v-else-if="displayValue >= (i-0.75)" class="rating-star" :icon="icons.rating_star_half"/>
        <Icon v-else class="rating-star" :icon="icons.rating_star_empty"/>
      </button>
    </div>
    <div v-if="!props.hideDetails && !props.readonly" class="form-input__details" :class="{'justify-center': props.center, 'justify-end': props.right}">
      <span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
      <span v-else-if="props.warning" class="form-input__message" tabindex="-1">{{ props.warning }}</span>
      <span v-else-if="props.info" class="form-input__message" tabindex="-1">{{ props.info }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {mdiStar, mdiStarHalfFull, mdiStarOutline} from "@mdi/js";
import {InputComponentEmits, type InputComponentProps, useInputComponent} from "../../../composables/useInputComponent";
import {ref, watch, computed} from "vue";

const props = withDefaults(defineProps<{
  // The amount of stars to display
  stars?: number,

  // The alignment
  left?: boolean,
  center?: boolean,
  right?: boolean,
} & InputComponentProps>(), {
  stars: 5
});
const emit = defineEmits([...InputComponentEmits, 'click']);

const icons = {
  rating_star_full: mdiStar,
  rating_star_half: mdiStarHalfFull,
  rating_star_empty: mdiStarOutline,
};

const {
  submitValue,
  sizeableString,
  isRequired,
  isEmpty,
  hasError,
  errorMessage
} = useInputComponent(props, emit);

const displayValue = computed(() => {
	return submitValue.value ?? (props.readonly ? props.stars : 0);
});

const classNames = computed(() => {
  let classNames: any[] = [{'rating-readonly': props.readonly}];

  if (sizeableString) {
    classNames.push('rating-' + sizeableString)
  }

  if (props.left) {
    classNames.push('justify-start')
  } else if (props.right) {
    classNames.push('justify-end')
  } else {
    classNames.push('justify-center') // Default
  }

  return classNames
});

function handleClickedValue(i) {
  if (!props.readonly) {
    submitValue.value = i;
    emit('click', i)
  }
}
</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.rating {
  display: flex;
  align-items: center;
}
.rating > button {
  background: none;
  border: none;
  outline: 0;
  cursor: pointer;
  padding: 0;
}
.rating > button:not(:first-child) {
  padding-left: 2px;
}
.rating > button:not(:last-child) {
  padding-right: 2px;
}
.rating > button .rating-star > svg {
  color: #ffdf00;
  fill: #ffdf00;
  width: 24px;
  height: 24px;
}
.rating.rating-readonly > button {
  cursor: default;
}
.rating.rating-xs > button:not(:first-child) {
  padding-left: 1px;
}
.rating.rating-xs > button:not(:last-child) {
  padding-right: 1px;
}
.rating.rating-xs > button .rating-star > svg {
  width: 16px;
  height: 16px;
}
.rating.rating-sm > button:not(:first-child) {
  padding-left: 1.5px;
}
.rating.rating-sm > button:not(:last-child) {
  padding-right: 1.5px;
}
.rating.rating-sm > button .rating-star > svg {
  width: 20px;
  height: 20px;
}
.rating.rating-lg > button .rating-star > svg {
  width: 28px;
  height: 28px;
}
.rating.rating-xl > button .rating-star > svg {
  width: 42px;
  height: 42px;
}
</style>